import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from "react";
import { Pencil, Power, Trash2 } from "lucide-react";
import { canManagePlans } from "../lib/access";
import { ApiError } from "../lib/api/client";
import {
  createPlan,
  deletePlan,
  listPlans,
  listSports,
  togglePlanStatus,
  updatePlan,
} from "../lib/api/endpoints";
import { money } from "../lib/format";
import { planBillingPeriodLabel } from "../lib/enums";
import { useSession } from "../lib/session/SessionContext";
import { C, fieldStyle } from "../theme";
import type { CreatePlanRequest, PlanDto, SportDto } from "../types";
import { Button } from "./Button";
import { Card } from "./Card";
import { ConfirmDialog } from "./ConfirmDialog";
import { FieldInput, FieldTextarea } from "./Field";
import { Label } from "./Label";

const PLAN_PAGE_SIZE = 10;
const SPORTS_PAGE_SIZE = 100;

const PERIOD_FILTER_OPTIONS = [
  { value: "", label: "Toda periodicidade" },
  { value: "1", label: "Semanal" },
  { value: "2", label: "Mensal" },
];

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.detail ?? error.message : fallback;
}

function labelStyle(): CSSProperties {
  return { display: "block", color: C.gray, fontSize: 12, fontWeight: 600, margin: "16px 0 4px" };
}

function errorTextStyle(): CSSProperties {
  return { color: C.red, fontSize: 11.5, marginTop: 8 };
}

function PlanForm({
  initialPlan,
  sports,
  submitting,
  submitError,
  onSubmit,
  onCancel,
}: {
  initialPlan?: PlanDto | null;
  sports: SportDto[];
  submitting: boolean;
  submitError: string | null;
  onSubmit: (payload: CreatePlanRequest) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initialPlan?.name ?? "");
  const [description, setDescription] = useState(initialPlan?.description ?? "");
  const [billingPeriod, setBillingPeriod] = useState(String(initialPlan?.billingPeriod ?? 2));
  const [price, setPrice] = useState(initialPlan ? String(initialPlan.price) : "");
  const [isActive, setIsActive] = useState(initialPlan?.isActive ?? true);
  const [sportIds, setSportIds] = useState<string[]>(initialPlan?.sportIds ?? []);
  const [errors, setErrors] = useState<{ name?: string; price?: string; sportIds?: string; description?: string }>({});
  const isEditing = Boolean(initialPlan);

  const toggleSport = (sportId: string) => {
    setSportIds(current => current.includes(sportId) ? current.filter(value => value !== sportId) : [...current, sportId]);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    const parsedPrice = Number(price);
    const nextErrors: { name?: string; price?: string; sportIds?: string; description?: string } = {};
    if (!trimmedName) nextErrors.name = "Informe o nome do plano";
    if (price === "" || !Number.isFinite(parsedPrice) || parsedPrice < 0) nextErrors.price = "Informe um preço válido";
    if (trimmedDescription.length > 2000) nextErrors.description = "Descrição muito longa (máx. 2000 caracteres)";
    if (sportIds.length === 0) nextErrors.sportIds = "Selecione ao menos uma modalidade";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    const payload: CreatePlanRequest = {
      name: trimmedName,
      description: trimmedDescription || null,
      billingPeriod: Number(billingPeriod),
      price: parsedPrice,
      sportIds,
    };
    if (!isEditing) payload.isActive = isActive;
    onSubmit(payload);
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="plan-form-title" style={{ position: "fixed", inset: 0, background: C.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
      <Card style={{ width: "100%", maxWidth: 560, maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
          <div id="plan-form-title"><Label>{isEditing ? "Editar plano" : "Novo plano"}</Label></div>
          <Button variant="ghost" onClick={onCancel} disabled={submitting} ariaLabel="Fechar">X</Button>
        </div>
        <form onSubmit={submit} noValidate>
          <label htmlFor="plan-name" style={labelStyle()}>Nome do plano</label>
          <FieldInput id="plan-name" value={name} maxLength={120} disabled={submitting} onChange={event => setName(event.target.value)} />
          {errors.name && <div role="alert" style={errorTextStyle()}>{errors.name}</div>}

          <label htmlFor="plan-description" style={labelStyle()}>Descrição</label>
          <FieldTextarea id="plan-description" value={description} maxLength={2000} disabled={submitting} onChange={event => setDescription(event.target.value)} />
          {errors.description && <div role="alert" style={errorTextStyle()}>{errors.description}</div>}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12 }}>
            <div>
              <label htmlFor="plan-billing" style={labelStyle()}>Periodicidade</label>
              <select id="plan-billing" value={billingPeriod} disabled={submitting} onChange={event => setBillingPeriod(event.target.value)} style={{ ...fieldStyle, width: "100%", padding: 10 }}>
                <option value="1">Semanal</option>
                <option value="2">Mensal</option>
              </select>
            </div>
            <div>
              <label htmlFor="plan-price" style={labelStyle()}>Preço (R$)</label>
              <FieldInput id="plan-price" type="number" min="0" step="0.01" value={price} disabled={submitting} onChange={event => setPrice(event.target.value)} />
              {errors.price && <div role="alert" style={errorTextStyle()}>{errors.price}</div>}
            </div>
          </div>

          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <legend style={{ ...labelStyle(), marginTop: 16 }}>Modalidades incluídas</legend>
            {sports.length === 0 ? (
              <div style={{ color: C.light, fontSize: 12.5 }}>Nenhuma modalidade ativa disponível.</div>
            ) : (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {sports.map(sport => (
                  <label key={sport.id} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: C.gray, border: `1px solid ${sportIds.includes(sport.id) ? C.ink : C.faint}`, borderRadius: 16, padding: "6px 10px", cursor: "pointer" }}>
                    <input type="checkbox" checked={sportIds.includes(sport.id)} disabled={submitting} onChange={() => toggleSport(sport.id)} />
                    {sport.name}
                  </label>
                ))}
              </div>
            )}
            {errors.sportIds && <div role="alert" style={errorTextStyle()}>{errors.sportIds}</div>}
          </fieldset>

          {!isEditing && (
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12.5, color: C.gray, marginTop: 16 }}>
              <input type="checkbox" checked={isActive} disabled={submitting} onChange={event => setIsActive(event.target.checked)} />
              Plano ativo
            </label>
          )}

          {submitError && <div role="alert" style={errorTextStyle()}>{submitError}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
            <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancelar</Button>
            <Button type="submit" disabled={submitting}>{submitting ? "Salvando..." : isEditing ? "Salvar plano" : "Criar plano"}</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function SportChips({ plan, sports }: { plan: PlanDto; sports: SportDto[] }) {
  const byId = new Map(sports.map(sport => [sport.id, sport.name]));
  const names = plan.sportIds.map(id => byId.get(id)).filter((name): name is string => Boolean(name));
  if (names.length === 0) return <span style={{ color: C.light, fontSize: 11.5 }}>{plan.sportIds.length} modalidade(s)</span>;
  return <>{names.map(name => <span key={name} style={{ color: C.gray, background: C.bg, borderRadius: 10, padding: "2px 7px", fontSize: 10.5, fontWeight: 700 }}>{name}</span>)}</>;
}

export function PlanManagementTab() {
  const { accessToken, activeTenant } = useSession();
  const token = accessToken ?? "";
  const canManage = canManagePlans(activeTenant?.roleSlug ?? null);
  const mutationRef = useRef(false);
  const [plans, setPlans] = useState<PlanDto[] | null>(null);
  const [sports, setSports] = useState<SportDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [periodFilter, setPeriodFilter] = useState("");
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formPlan, setFormPlan] = useState<PlanDto | null | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<PlanDto | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PlanDto | null>(null);
  const [processing, setProcessing] = useState(false);
  const active = statusFilter === "all" ? undefined : statusFilter === "active";
  const billingPeriod = periodFilter === "" ? undefined : Number(periodFilter);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setPlans(null);
    setLoadError(null);
    listPlans(token, { page, pageSize: PLAN_PAGE_SIZE, isActive: active, billingPeriod }).then(result => {
      if (!cancelled) {
        setPlans(result.items);
        setTotalCount(result.totalCount);
      }
    }).catch(error => {
      if (!cancelled) {
        setPlans([]);
        setLoadError(errorMessage(error, "Falha ao carregar planos."));
      }
    });
    return () => { cancelled = true; };
  }, [token, page, active, billingPeriod, reloadKey]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    listSports(token, { page: 1, pageSize: SPORTS_PAGE_SIZE, isActive: true }).then(result => {
      if (!cancelled) setSports(result.items);
    }).catch(() => {
      if (!cancelled) setSports([]);
    });
    return () => { cancelled = true; };
  }, [token]);

  const submitPlan = async (payload: CreatePlanRequest) => {
    if (!token || mutationRef.current) return;
    mutationRef.current = true;
    setSubmitting(true);
    setActionError(null);
    try {
      const saved = formPlan ? await updatePlan(token, formPlan.id, payload) : await createPlan(token, payload);
      setPlans(current => formPlan ? current?.map(plan => plan.id === saved.id ? saved : plan) ?? [] : [saved, ...(current ?? [])].slice(0, PLAN_PAGE_SIZE));
      setTotalCount(current => formPlan ? current : current + 1);
      setFormPlan(undefined);
      setSuccess(formPlan ? "Plano atualizado com sucesso." : "Plano criado com sucesso.");
    } catch (error) {
      setActionError(errorMessage(error, "Falha ao salvar plano."));
    } finally {
      mutationRef.current = false;
      setSubmitting(false);
    }
  };

  const confirmStatus = async () => {
    if (!token || !pendingStatus || mutationRef.current) return;
    mutationRef.current = true;
    setProcessing(true);
    setActionError(null);
    try {
      const saved = await togglePlanStatus(token, pendingStatus.id, !pendingStatus.isActive);
      setPlans(current => current?.map(plan => plan.id === saved.id ? saved : plan) ?? []);
      setPendingStatus(null);
      setSuccess(`Plano ${saved.isActive ? "ativado" : "desativado"} com sucesso.`);
    } catch (error) {
      setActionError(errorMessage(error, "Falha ao alterar o status do plano."));
    } finally {
      mutationRef.current = false;
      setProcessing(false);
    }
  };

  const confirmDelete = async () => {
    if (!token || !pendingDelete || mutationRef.current) return;
    mutationRef.current = true;
    setProcessing(true);
    setActionError(null);
    try {
      await deletePlan(token, pendingDelete.id);
      setPlans(current => current?.filter(plan => plan.id !== pendingDelete.id) ?? []);
      setTotalCount(current => Math.max(0, current - 1));
      setPendingDelete(null);
      setSuccess("Plano excluído com sucesso.");
    } catch (error) {
      setActionError(errorMessage(error, "Falha ao excluir plano."));
    } finally {
      mutationRef.current = false;
      setProcessing(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PLAN_PAGE_SIZE));

  return <div style={{ display: "grid", gap: 14 }}>
    {success && <div role="status" style={{ color: C.ink, background: C.limeSoft, borderRadius: 10, padding: 12, fontSize: 12.5 }}>{success}</div>}
    {loadError && <div role="alert" style={{ color: C.red, background: C.white, border: `1px solid ${C.faint}`, borderRadius: 10, padding: 12, fontSize: 12.5 }}>{loadError} <Button variant="ghost" onClick={() => setReloadKey(value => value + 1)}>Tentar novamente</Button></div>}
    {actionError && !formPlan && <div role="alert" style={{ color: C.red, fontSize: 12 }}>{actionError}</div>}
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div><Label>Gestão de planos</Label><div style={{ color: C.light, fontSize: 12, marginTop: 3 }}>Configure os planos de contratação e as modalidades incluídas.</div></div>
        {canManage ? <Button onClick={() => { setActionError(null); setFormPlan(null); }}>+ Novo plano</Button> : <span style={{ color: C.light, background: C.bg, borderRadius: 12, padding: "5px 9px", fontSize: 11.5 }}>Somente leitura</span>}
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
        <select aria-label="Filtrar planos por status" value={statusFilter} onChange={event => { setStatusFilter(event.target.value); setPage(1); }} style={fieldStyle}>
          <option value="all">Todos os status</option>
          <option value="active">Ativos</option>
          <option value="inactive">Inativos</option>
        </select>
        <select aria-label="Filtrar planos por periodicidade" value={periodFilter} onChange={event => { setPeriodFilter(event.target.value); setPage(1); }} style={fieldStyle}>
          {PERIOD_FILTER_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </div>
      {plans === null && !loadError && <div style={{ color: C.light, padding: "22px 0", fontSize: 13 }}>Carregando planos...</div>}
      {plans?.length === 0 && !loadError && <div style={{ color: C.light, padding: "22px 0", fontSize: 13 }}>Nenhum plano encontrado.</div>}
      {plans && plans.length > 0 && <div style={{ display: "grid", gap: 8, marginTop: 14 }}>{plans.map(plan => (
        <div key={plan.id} style={{ border: `1px solid ${C.faint}`, borderRadius: 10, padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div style={{ display: "grid", gap: 4 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <b style={{ color: C.ink, fontSize: 13.5 }}>{plan.name}</b>
              <span style={{ color: plan.isActive ? C.ink : C.light, background: plan.isActive ? C.limeSoft : C.bg, borderRadius: 10, padding: "2px 7px", fontSize: 10.5, fontWeight: 700 }}>{plan.isActive ? "Ativo" : "Inativo"}</span>
              <span style={{ color: C.gray, background: C.bg, borderRadius: 10, padding: "2px 7px", fontSize: 10.5, fontWeight: 700 }}>{planBillingPeriodLabel(plan.billingPeriod)}</span>
              <span style={{ color: C.gray, fontSize: 12.5, fontWeight: 600 }}>{money(plan.price)}</span>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              <SportChips plan={plan} sports={sports} />
              {plan.description && <span style={{ color: C.light, fontSize: 11.5 }}>{plan.description}</span>}
            </div>
          </div>
          {canManage && <div style={{ display: "flex", gap: 8 }}>
            <Button variant="ghost" ariaLabel={`Editar ${plan.name}`} title="Editar" onClick={() => { setActionError(null); setFormPlan(plan); }}><Pencil size={16} /></Button>
            <Button variant="ghost" ariaLabel={`${plan.isActive ? "Desativar" : "Ativar"} ${plan.name}`} title={plan.isActive ? "Desativar" : "Ativar"} onClick={() => setPendingStatus(plan)}><Power size={16} /></Button>
            <Button variant="ghost" ariaLabel={`Excluir ${plan.name}`} title="Excluir" onClick={() => setPendingDelete(plan)}><Trash2 size={16} /></Button>
          </div>}
        </div>
      ))}</div>}
      {totalCount > PLAN_PAGE_SIZE && <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14, alignItems: "center" }}>
        <span style={{ color: C.light, fontSize: 12 }}>Página {page} de {totalPages}</span>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="outline" disabled={page === 1} onClick={() => setPage(value => value - 1)}>Anterior</Button>
          <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage(value => value + 1)}>Próxima</Button>
        </div>
      </div>}
    </Card>
    {formPlan !== undefined && <PlanForm initialPlan={formPlan} sports={sports} submitting={submitting} submitError={actionError} onSubmit={submitPlan} onCancel={() => !submitting && setFormPlan(undefined)} />}
    {pendingStatus && <ConfirmDialog title={`${pendingStatus.isActive ? "Desativar" : "Ativar"} plano`} confirmLabel={`${pendingStatus.isActive ? "Desativar" : "Ativar"} plano`} destructive={pendingStatus.isActive} busy={processing} onConfirm={confirmStatus} onCancel={() => !processing && setPendingStatus(null)}>Deseja {pendingStatus.isActive ? "desativar" : "ativar"} {pendingStatus.name}?</ConfirmDialog>}
    {pendingDelete && <ConfirmDialog title="Excluir plano" confirmLabel="Excluir plano" busy={processing} onConfirm={confirmDelete} onCancel={() => !processing && setPendingDelete(null)}>Deseja excluir {pendingDelete.name}? Esta ação não pode ser desfeita.</ConfirmDialog>}
  </div>;
}
